import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useProjects } from '../context/ProjectContext';
import { API_URL } from '../config/api';

function DebugView() {
  const { user } = useAuth();
  const { selectedProject } = useProjects();
  const [validationResult, setValidationResult] = useState(null);
  const [validationMessage, setValidationMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Emission calculation state
  const [calcPayload, setCalcPayload] = useState({
    projectId: undefined,
    activityId: '',
    region: '',
    unit: 'KM',
    date: '',
    energy: 0,
    energyUnit: 'KWH',
    distance: 0,
    distanceUnit: 'KM',
    weight: 0,
    weightUnit: 'KG',
    volume: 0,
    volumeUnit: 'M3',
    tkm: 0,
    tkmUnit: 'TKM'
  });
  const [isCalculating, setIsCalculating] = useState(false);
  const [calcResult, setCalcResult] = useState(null);
  const [calcError, setCalcError] = useState('');

  // Prefill projectId from currently selected project
  useEffect(() => {
    if (selectedProject?.id) {
      setCalcPayload((prev) => ({ ...prev, projectId: selectedProject.id }));
    }
  }, [selectedProject]);

  const handleValidate = async () => {
    if (!user?.token) {
      console.error('No authentication token available');
      return;
    }

    setIsLoading(true);
    setValidationResult(null);
    setValidationMessage('');

    try {
      const response = await fetch(`${API_URL}/api/auth/validate`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(`Expected JSON response but got ${contentType || 'unknown content type'}`);
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setValidationResult(data.valid);
      setValidationMessage(data.message || '');
    } catch (error) {
      console.error('Validation error:', error);
      // Set a more specific error state
      setValidationResult('error');
      setValidationMessage('Network error or invalid response');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCalcChange = (e) => {
    const { name, value } = e.target;
    const numericFields = new Set(['projectId', 'energy', 'distance', 'weight', 'volume', 'tkm']);
    let newValue = value;
    if (numericFields.has(name)) {
      // Allow empty string to clear field; otherwise parse number
      newValue = value === '' ? undefined : Number(value);
    }
    setCalcPayload((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleCalculate = async () => {
    if (!user?.token) {
      console.error('No authentication token available');
      return;
    }
    setIsCalculating(true);
    setCalcResult(null);
    setCalcError('');
    try {
      const response = await fetch(`${API_URL}/api/emissions/calculate`, {
        method: 'POST',
        headers: {
          'Authorization': `Api-Key cak_live_olvCwIgEY_YoomXiE4T8r3R7r9c7EZ-B8ATeuhJIeXR0GZOQlHNePk`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(calcPayload)
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(`Expected JSON response but got ${contentType || 'unknown content type'}`);
      }
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setCalcResult(data);
    } catch (error) {
      console.error('Calculation error:', error);
      setCalcError(error.message || 'Network error or invalid response');
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Debug View</h1>
      </div>

      {/* Main Content */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Debug Tools</h2>
          <p className="text-gray-600 mb-6">
            Use this page for debugging and validation purposes.
          </p>
          
          <button
            onClick={handleValidate}
            disabled={isLoading || !user?.token}
            className={`px-6 py-3 rounded-lg transition-colors font-medium ${
              isLoading || !user?.token
                ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isLoading ? 'Validating...' : 'Validate'}
          </button>

          {/* Validation Result Display */}
          {validationResult !== null && (
            <div className="mt-6">
              {validationResult === 'error' ? (
                <div className="inline-flex items-center px-4 py-2 rounded-lg bg-yellow-100 text-yellow-800 border border-yellow-200">
                  <div className="w-3 h-3 rounded-full mr-2 bg-yellow-500"></div>
                  <span className="font-medium">{validationMessage}</span>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className={`inline-flex items-center px-4 py-2 rounded-lg ${
                    validationResult 
                      ? 'bg-green-100 text-green-800 border border-green-200' 
                      : 'bg-red-100 text-red-800 border border-red-200'
                  }`}>
                    <div className={`w-3 h-3 rounded-full mr-2 ${
                      validationResult ? 'bg-green-500' : 'bg-red-500'
                    }`}></div>
                    <span className="font-medium">
                      {validationResult ? 'True' : 'False'}
                    </span>
                  </div>
                  {validationMessage && (
                    <div className="text-sm text-gray-600 mt-2">
                      {validationMessage}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {!user?.token && (
            <div className="mt-4 text-amber-600 text-sm">
              No authentication token available
            </div>
          )}
        </div>
      </div>

      {/* Emission Calculation */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mt-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Emission Calculation</h2>
        <p className="text-gray-600 mb-6">Wypełnij pola i wyślij zapytanie POST do /api/emissions/calculate.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">projectId</label>
            <input
              type="number"
              name="projectId"
              value={calcPayload.projectId ?? ''}
              onChange={handleCalcChange}
              placeholder={selectedProject?.id ? String(selectedProject.id) : 'podaj projectId'}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">activityId</label>
            <input
              type="text"
              name="activityId"
              value={calcPayload.activityId}
              onChange={handleCalcChange}
              placeholder="wklej activityId"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">region</label>
            <input
              type="text"
              name="region"
              value={calcPayload.region}
              onChange={handleCalcChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">unit</label>
            <input
              type="text"
              name="unit"
              value={calcPayload.unit}
              onChange={handleCalcChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">date</label>
            <input
              type="date"
              name="date"
              value={calcPayload.date}
              onChange={handleCalcChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">energy</label>
            <input
              type="number"
              step="any"
              name="energy"
              value={calcPayload.energy ?? ''}
              onChange={handleCalcChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">energyUnit</label>
            <input
              type="text"
              name="energyUnit"
              value={calcPayload.energyUnit}
              onChange={handleCalcChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">distance</label>
            <input
              type="number"
              step="any"
              name="distance"
              value={calcPayload.distance ?? ''}
              onChange={handleCalcChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">distanceUnit</label>
            <input
              type="text"
              name="distanceUnit"
              value={calcPayload.distanceUnit}
              onChange={handleCalcChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">weight</label>
            <input
              type="number"
              step="any"
              name="weight"
              value={calcPayload.weight ?? ''}
              onChange={handleCalcChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">weightUnit</label>
            <input
              type="text"
              name="weightUnit"
              value={calcPayload.weightUnit}
              onChange={handleCalcChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">volume</label>
            <input
              type="number"
              step="any"
              name="volume"
              value={calcPayload.volume ?? ''}
              onChange={handleCalcChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">volumeUnit</label>
            <input
              type="text"
              name="volumeUnit"
              value={calcPayload.volumeUnit}
              onChange={handleCalcChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">tkm</label>
            <input
              type="number"
              step="any"
              name="tkm"
              value={calcPayload.tkm ?? ''}
              onChange={handleCalcChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">tkmUnit</label>
            <input
              type="text"
              name="tkmUnit"
              value={calcPayload.tkmUnit}
              onChange={handleCalcChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={handleCalculate}
            disabled={isCalculating || !user?.token}
            className={`px-6 py-3 rounded-lg transition-colors font-medium ${
              isCalculating || !user?.token
                ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isCalculating ? 'Calculating...' : 'Calculate Emissions'}
          </button>
          {!user?.token && (
            <div className="text-amber-600 text-sm">Brak tokena uwierzytelniającego</div>
          )}
        </div>

        {/* Result / Error */}
        {calcError && (
          <div className="mt-6 inline-flex items-center px-4 py-2 rounded-lg bg-yellow-100 text-yellow-800 border border-yellow-200">
            <div className="w-3 h-3 rounded-full mr-2 bg-yellow-500"></div>
            <span className="font-medium">{calcError}</span>
          </div>
        )}

        {calcResult && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Wynik</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-3 rounded-md bg-gray-50 border border-gray-200">
                <div className="text-sm text-gray-500">co2e</div>
                <div className="font-mono text-gray-900">{String(calcResult.co2e)}</div>
              </div>
              <div className="p-3 rounded-md bg-gray-50 border border-gray-200">
                <div className="text-sm text-gray-500">co2eUnit</div>
                <div className="font-mono text-gray-900">{calcResult.co2eUnit}</div>
              </div>
              <div className="p-3 rounded-md bg-gray-50 border border-gray-200">
                <div className="text-sm text-gray-500">normalizedInput</div>
                <div className="font-mono text-gray-900">{String(calcResult.normalizedInput)}</div>
              </div>
              <div className="p-3 rounded-md bg-gray-50 border border-gray-200">
                <div className="text-sm text-gray-500">normalizedInputUnit</div>
                <div className="font-mono text-gray-900">{calcResult.normalizedInputUnit}</div>
              </div>
              <div className="p-3 rounded-md bg-gray-50 border border-gray-200">
                <div className="text-sm text-gray-500">appliedRegion</div>
                <div className="font-mono text-gray-900">{calcResult.appliedRegion}</div>
              </div>
            </div>
            <details className="mt-4">
              <summary className="cursor-pointer text-sm text-gray-600">Pokaż surowe JSON</summary>
              <pre className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-md overflow-auto text-sm text-gray-800">{JSON.stringify(calcResult, null, 2)}</pre>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}

export default DebugView;
